import React, { useState } from 'react';
import { useNavigate } from 'react-router-dom';
import { Play, ArrowRight } from 'lucide-react';
import { Accordion, AccordionContent, AccordionItem, AccordionTrigger } from './ui/accordion';

const getYouTubeId = (input) => {
  if (!input) return '';
  const m = String(input).match(/(?:youtube\.com\/(?:watch\?v=|embed\/|shorts\/)|youtu\.be\/)([\w-]{11})/);
  return m ? m[1] : String(input).trim();
};

export const SECTION_TYPES = [
  { type: 'text', label: 'Text Box' },
  { type: 'text_image', label: 'Text + Image' },
  { type: 'image_banner', label: 'Image Banner' },
  { type: 'gallery_album', label: 'Gallery Album (chapters)' },
  { type: 'youtube_strip', label: 'YouTube Strip' },
  { type: 'cta_banner', label: 'CTA Banner' },
  { type: 'stats_band', label: 'Stats Band' },
  { type: 'faq', label: 'FAQ Accordion' }
];

export const emptySectionData = (type) => ({
  text: { kicker: '', heading: '', body: '', align: 'left' },
  text_image: { kicker: '', heading: '', body: '', image: '', image_side: 'right', cta_label: '', cta_url: '' },
  image_banner: { image: '', heading: '', caption: '' },
  gallery_album: { kicker: '', heading: '', note: '', chapters: [{ name: 'Album 1', photos: [] }] },
  youtube_strip: { kicker: '', heading: '', videos: [] },
  cta_banner: { heading: '', copy: '', cta_label: '', cta_url: '', style: 'green' },
  stats_band: { stats: [{ value: '', label: '' }] },
  faq: { heading: '', items: [{ q: '', a: '' }] }
}[type] || {});

const Kicker = ({ children }) => children
  ? <p className="text-[#C6A87C] uppercase tracking-[0.25em] text-[11px] font-semibold">{children}</p> : null;
const Heading = ({ children, light }) => children
  ? <h2 className={`font-wvc-display text-2xl md:text-4xl font-medium tracking-tight mt-3 ${light ? 'text-white' : 'text-[#1A201C]'}`}>{children}</h2> : null;

const TextBlock = ({ data }) => (
  <section className={`max-w-6xl mx-auto px-4 sm:px-6 py-10 md:py-14 ${data.align === 'center' ? 'text-center' : ''}`}>
    <div className={data.align === 'center' ? 'max-w-3xl mx-auto' : 'max-w-3xl'}>
      <Kicker>{data.kicker}</Kicker>
      <Heading>{data.heading}</Heading>
      {data.body && <p className="text-[#3E4C43] mt-4 text-sm md:text-base leading-relaxed whitespace-pre-line">{data.body}</p>}
    </div>
  </section>
);

const TextImageBlock = ({ data }) => {
  const navigate = useNavigate();
  return (
    <section className="max-w-6xl mx-auto px-4 sm:px-6 py-10 md:py-14">
      <div className={`grid grid-cols-1 lg:grid-cols-2 gap-8 items-center ${data.image_side === 'left' ? 'lg:[direction:rtl]' : ''}`}>
        <div className="lg:[direction:ltr]">
          <Kicker>{data.kicker}</Kicker>
          <Heading>{data.heading}</Heading>
          {data.body && <p className="text-[#3E4C43] mt-4 text-sm leading-relaxed whitespace-pre-line">{data.body}</p>}
          {data.cta_label && (
            <button
              onClick={() => data.cta_url?.startsWith('http') ? window.open(data.cta_url, '_blank') : navigate(data.cta_url || '/')}
              className="mt-6 bg-[#1A3626] text-white hover:bg-[#254834] rounded-full px-7 py-3.5 text-sm inline-flex items-center gap-2 transition-colors"
            >
              {data.cta_label} <ArrowRight className="w-4 h-4" strokeWidth={1.5} />
            </button>
          )}
        </div>
        {data.image && (
          <div className="lg:[direction:ltr] aspect-[4/3] rounded-2xl overflow-hidden bg-[#F3F1E7]">
            <img src={data.image} alt={data.heading || 'Section'} className="w-full h-full object-cover" loading="lazy" />
          </div>
        )}
      </div>
    </section>
  );
};

const ImageBannerBlock = ({ data }) => (
  <section className="relative">
    <div className="h-[45vh] md:h-[60vh] overflow-hidden">
      {data.image && <img src={data.image} alt={data.heading || 'Banner'} className="w-full h-full object-cover" loading="lazy" />}
      <div className="absolute inset-0 bg-gradient-to-t from-[#12160F]/70 via-transparent to-transparent" />
    </div>
    {(data.heading || data.caption) && (
      <div className="absolute bottom-6 left-0 right-0">
        <div className="max-w-6xl mx-auto px-4 sm:px-6">
          {data.heading && <h2 className="font-wvc-display text-2xl md:text-4xl text-white font-medium tracking-tight">{data.heading}</h2>}
          {data.caption && <p className="text-white/75 text-sm mt-1">{data.caption}</p>}
        </div>
      </div>
    )}
  </section>
);

const GalleryAlbumBlock = ({ data }) => {
  const chapters = data.chapters || [];
  const [active, setActive] = useState(0);
  if (!chapters.length) return null;
  return (
    <section className="max-w-6xl mx-auto px-4 sm:px-6 py-10 md:py-14">
      <Kicker>{data.kicker}</Kicker>
      <Heading>{data.heading}</Heading>
      {data.note && <p className="text-[#3E4C43] mt-3 text-sm">{data.note}</p>}
      <div className="flex gap-2 flex-wrap mt-6">
        {chapters.map((c, i) => (
          <button
            key={i}
            onClick={() => setActive(i)}
            className={`px-4 py-1.5 rounded-full text-[13px] transition-colors ${active === i ? 'bg-[#1A3626] text-white' : 'bg-[#F3F1E7] text-[#3E4C43] hover:bg-[#EBE8D8]'}`}
          >
            {c.name}
          </button>
        ))}
      </div>
      <div className="mt-4 grid grid-cols-2 md:grid-cols-3 gap-3">
        {(chapters[active]?.photos || []).map((url, i) => (
          <div key={i} className="aspect-[4/3] rounded-xl overflow-hidden bg-[#F3F1E7]">
            <img src={url} alt={`${chapters[active]?.name} ${i + 1}`} className="w-full h-full object-cover hover:scale-105 transition-transform duration-500" loading="lazy" />
          </div>
        ))}
      </div>
    </section>
  );
};

const YouTubeStripBlock = ({ data }) => {
  const [playing, setPlaying] = useState(null);
  const videos = (data.videos || []).map(getYouTubeId).filter(Boolean);
  if (!videos.length) return null;
  return (
    <section className="max-w-6xl mx-auto px-4 sm:px-6 py-10 md:py-14">
      <Kicker>{data.kicker}</Kicker>
      <Heading>{data.heading}</Heading>
      <div className="grid grid-cols-2 md:grid-cols-3 gap-3 mt-6">
        {videos.map((vid, i) => (
          <div key={vid} className="aspect-video rounded-xl overflow-hidden bg-[#F3F1E7] relative group">
            {playing === vid ? (
              <iframe
                src={`https://www.youtube.com/embed/${vid}?rel=0&autoplay=1&cc_load_policy=0`}
                title={`Video ${i + 1}`}
                className="w-full h-full border-0"
                allow="autoplay; encrypted-media; picture-in-picture"
                allowFullScreen
              />
            ) : (
              <button onClick={() => setPlaying(vid)} className="w-full h-full relative">
                <img src={`https://i.ytimg.com/vi/${vid}/hqdefault.jpg`} alt={`Video ${i + 1}`} className="w-full h-full object-cover" loading="lazy" />
                <span className="absolute inset-0 flex items-center justify-center bg-[#12160F]/25 group-hover:bg-[#12160F]/45 transition-colors">
                  <span className="w-11 h-11 rounded-full bg-white/95 flex items-center justify-center">
                    <Play className="w-4 h-4 text-[#1A3626] ml-0.5" fill="currentColor" />
                  </span>
                </span>
              </button>
            )}
          </div>
        ))}
      </div>
    </section>
  );
};

const CtaBannerBlock = ({ data }) => {
  const navigate = useNavigate();
  const green = data.style !== 'light';
  return (
    <section className={green ? 'bg-[#1A3626]' : 'bg-[#F3F1E7]'}>
      <div className="max-w-3xl mx-auto px-4 sm:px-6 py-12 md:py-16 text-center">
        <h2 className={`font-wvc-display text-2xl md:text-4xl font-medium tracking-tight ${green ? 'text-white' : 'text-[#1A201C]'}`}>{data.heading}</h2>
        {data.copy && <p className={`mt-3 text-sm ${green ? 'text-white/70' : 'text-[#3E4C43]'}`}>{data.copy}</p>}
        {data.cta_label && (
          <button
            onClick={() => data.cta_url?.startsWith('http') ? window.open(data.cta_url, '_blank') : navigate(data.cta_url || '/')}
            className={`mt-6 rounded-full px-9 py-4 text-xs uppercase tracking-[0.2em] font-semibold transition-colors ${green ? 'bg-white text-[#1A3626] hover:bg-[#C6A87C] hover:text-white' : 'bg-[#1A3626] text-white hover:bg-[#254834]'}`}
          >
            {data.cta_label}
          </button>
        )}
      </div>
    </section>
  );
};

const StatsBandBlock = ({ data }) => (
  <section className="bg-[#1A3626]">
    <div className="max-w-6xl mx-auto px-4 sm:px-6 grid grid-cols-2 md:grid-cols-4 divide-x divide-white/10">
      {(data.stats || []).map((s, i) => (
        <div key={i} className="py-5 md:py-7 px-4 first:pl-0">
          <div className="font-wvc-display text-2xl md:text-4xl text-white">{s.value}</div>
          <div className="text-[9px] md:text-[10px] uppercase tracking-[0.18em] text-[#E5D8C6]/80 mt-1.5">{s.label}</div>
        </div>
      ))}
    </div>
  </section>
);

const FaqBlock = ({ data }) => (
  <section className="max-w-4xl mx-auto px-4 sm:px-6 py-10 md:py-14">
    <Heading>{data.heading}</Heading>
    <Accordion type="single" collapsible className="mt-5 border-t border-[#1A3626]/10">
      {(data.items || []).map((f, i) => (
        <AccordionItem key={i} value={`cfaq-${i}`} className="border-b border-[#1A3626]/10">
          <AccordionTrigger className="text-left font-wvc-display text-lg text-[#1A201C] py-4 hover:no-underline">{f.q}</AccordionTrigger>
          <AccordionContent className="text-[#3E4C43] text-sm leading-relaxed pb-4">{f.a}</AccordionContent>
        </AccordionItem>
      ))}
    </Accordion>
  </section>
);

const RENDERERS = {
  text: TextBlock,
  text_image: TextImageBlock,
  image_banner: ImageBannerBlock,
  gallery_album: GalleryAlbumBlock,
  youtube_strip: YouTubeStripBlock,
  cta_banner: CtaBannerBlock,
  stats_band: StatsBandBlock,
  faq: FaqBlock
};

export const CustomSection = ({ section }) => {
  if (!section || section.visible === false) return null;
  const Renderer = RENDERERS[section.type];
  if (!Renderer) return null;
  return (
    <div className="font-wvc-body" data-testid={`custom-section-${section.id || section.key}`}>
      <Renderer data={section.data || {}} />
    </div>
  );
};
